import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useShopifyLanguageSync } from '../../../i18n/useShopifyLanguageSync';
import { IEntreprisesRepository } from "../types.ts";
import { FilterAccordion } from "../shared.ts";
import FilterAlphabetical from "../components/FilterAlphabetical.tsx";
import EntrepriseRow from "./EntrepriseRow.tsx";

interface SelectedFilters {
	industryTypes: string[];
	regions: string[];
	alphabetical: string[];
	polymerTypes: string[];
	productTypes: string[];
	operationTypes: string[];
}

export default function EntreprisesPage() {
	const { t } = useTranslation(['products', 'entreprises']);
	useShopifyLanguageSync();

	const entreprisesRepositoryData = window.entreprisesRepositoryData;
	const entreprisesPerPage = 9;

	// State management
	const [displayEntreprises, setDisplayEntreprises] = useState<IEntreprisesRepository[]>(entreprisesRepositoryData);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
		industryTypes: [],
		productTypes: [],
		operationTypes: [],
		regions: [],
		polymerTypes: [],
		alphabetical: [],
	});

	// Memoized filter data
	const alphabeticalOptions = useMemo(() => new Set(
		[...entreprisesRepositoryData
			.flatMap((entreprise) =>
				entreprise.name
					? entreprise.name.slice(0, 1)
						.normalize('NFD')
						.replace(/[\u0300-\u036f]/g, '')
						.toUpperCase()
					: []
			)
		].sort()
	), [entreprisesRepositoryData]);

	const industryTypes = useMemo(() => {
		const industryTypesMap = t('entreprises:industryTypes', { returnObjects: true }) as Record<string, string>;
		return new Set(Object.values(industryTypesMap));
	}, [t]);

	const productTypes = useMemo(() => {
		const productTypesMap = t('entreprises:productTypes', { returnObjects: true }) as Record<string, string>;
		return new Set(Object.values(productTypesMap));
	}, [t]);

	const operationTypes = useMemo(() => {
		const operationTypesMap = t('products:operationTypes', { returnObjects: true }) as Record<string, string>;
		return new Set(Object.values(operationTypesMap));
	}, [t]);

	const polymerTypes = useMemo(() => {
		const polymerTypesMap = t('products:polymerTypes', { returnObjects: true }) as Record<string, string>;
		return new Set(Object.values(polymerTypesMap));
	}, [t]);

	const regions = useMemo(() => {
		const regionsMap = t('entreprises:regions', { returnObjects: true }) as Record<string, string>;
		return new Set(Object.values(regionsMap));
	}, [t]);

	// Search functionality
	const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const newSearchTerm = event.target.value;
		setSearchTerm(newSearchTerm);
		setCurrentPage(1);
	}, []);

	const clearSearch = useCallback(() => {
		setSearchTerm('');
		setCurrentPage(1);
	}, []);

	// Filter handlers
	const createFilterHandler = useCallback((filterKey: keyof SelectedFilters) => {
		return (selectedValues: string[]) => {
			setSelectedFilters(prev => ({
				...prev,
				[filterKey]: selectedValues
			}));
			setCurrentPage(1);
		};
	}, []);

	// Helper functions to map selected translated values back to database values
	const getIndustryDatabaseValue = (translatedValue: string): string | null => {
		const industryTypesMap = t('entreprises:industryTypes', { returnObjects: true }) as Record<string, string>;
		for (const [dbValue, translatedValueFromMap] of Object.entries(industryTypesMap)) {
			if (translatedValueFromMap === translatedValue) {
				return dbValue;
			}
		}
		return null;
	};

	const getProductDatabaseValue = (translatedValue: string): string | null => {
		const productTypesMap = t('entreprises:productTypes', { returnObjects: true }) as Record<string, string>;
		for (const [dbValue, translatedValueFromMap] of Object.entries(productTypesMap)) {
			if (translatedValueFromMap === translatedValue) {
				return dbValue;
			}
		}
		return null;
	};

	const getOperationDatabaseValue = (translatedValue: string): string | null => {
		const operationTypesMap = t('products:operationTypes', { returnObjects: true }) as Record<string, string>;
		for (const [dbValue, translatedValueFromMap] of Object.entries(operationTypesMap)) {
			if (translatedValueFromMap === translatedValue) {
				return dbValue;
			}
		}
		return null;
	};

	const getPolymerDatabaseValue = (translatedValue: string): string | null => {
		const polymerTypesMap = t('products:polymerTypes', { returnObjects: true }) as Record<string, string>;
		for (const [dbValue, translatedValueFromMap] of Object.entries(polymerTypesMap)) {
			if (translatedValueFromMap === translatedValue) {
				return dbValue;
			}
		}
		return null;
	};

	const getRegionDatabaseValue = (translatedValue: string): string | null => {
		const regionsMap = t('entreprises:regions', { returnObjects: true }) as Record<string, string>;
		for (const [dbValue, translatedValueFromMap] of Object.entries(regionsMap)) {
			if (translatedValueFromMap === translatedValue) {
				return dbValue;
			}
		}
		return null;
	};

	// Filtering logic
	const filteredEntreprises = useMemo(() => {
		const { industryTypes, productTypes, operationTypes, regions, polymerTypes, alphabetical } = selectedFilters;

		return entreprisesRepositoryData.filter((entreprise) => {
			// Industry type filtering - convert selected translated values back to database values
			const matchesIndustryType = industryTypes.length === 0 || entreprise.type_industry.split(';').some((industry) => {
				const selectedDbValues = Array.from(industryTypes)
					.map(translatedValue => getIndustryDatabaseValue(translatedValue))
					.filter(dbValue => dbValue !== null);
				return selectedDbValues.includes(industry.trim());
			});

			// Product type filtering - convert selected translated values back to database values
			const matchesProductType = productTypes.length === 0 || entreprise.type_product.split(';').some((product) => {
				const selectedDbValues = Array.from(productTypes)
					.map(translatedValue => getProductDatabaseValue(translatedValue))
					.filter(dbValue => dbValue !== null);
				return selectedDbValues.includes(product.trim());
			});

			// Operation type filtering - convert selected translated values back to database values
			const matchesOperationType = operationTypes.length === 0 || entreprise.type_operation.split(';').some((operation) => {
				const selectedDbValues = Array.from(operationTypes)
					.map(translatedValue => getOperationDatabaseValue(translatedValue))
					.filter(dbValue => dbValue !== null);
				return selectedDbValues.includes(operation.trim());
			});

			// Polymer type filtering - convert selected translated values back to database values
			const matchesPolymerType = polymerTypes.length === 0 || entreprise.type_polymer.split(';').some((polymer) => {
				const selectedDbValues = Array.from(polymerTypes)
					.map(translatedValue => getPolymerDatabaseValue(translatedValue))
					.filter(dbValue => dbValue !== null);
				return selectedDbValues.includes(polymer.trim());
			});

			// Region filtering - convert selected translated values back to database values
			const matchesRegion = regions.length === 0 || (() => {
				const selectedDbValues = Array.from(regions)
					.map(translatedValue => getRegionDatabaseValue(translatedValue))
					.filter(dbValue => dbValue !== null);
				return selectedDbValues.includes(entreprise.region);
			})();
			const matchesAlphabetical = alphabetical.length === 0 || alphabetical.includes(entreprise.name.slice(0, 1).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase());

			// Search functionality - search in name, description, and region
			const matchesSearch = searchTerm === '' || [
				entreprise.name,
				entreprise.description,
				entreprise.region
			].some(field =>
				field && field.toLowerCase().includes(searchTerm.toLowerCase())
			);

			return matchesIndustryType && matchesProductType && matchesOperationType && matchesPolymerType && matchesRegion && matchesAlphabetical && matchesSearch;
		});
	}, [entreprisesRepositoryData, selectedFilters, searchTerm, t]);

	// Page change handler
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	// Effect to update displayEntreprises when filteredEntreprises changes
	useEffect(() => {
		setDisplayEntreprises(filteredEntreprises);
	}, [filteredEntreprises]);

	// Calculated values
	const maxPages = Math.ceil(displayEntreprises.length / entreprisesPerPage);

	return (
		<div className="wrapper">
			<h1 className="tw-my-16 tw-text-5xl tw-font-bold">{t('entreprises:title')}</h1>
				<div className="tw-mb-8">
					<div className="tw-relative">
						{/* Search Icon */}
						<div className="tw-absolute tw-left-4 tw-top-1/2 tw-transform tw-translate-y-[-50%] tw-pointer-events-none tw-z-10 tw-flex tw-items-center tw-justify-center">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{color: '#6B7280'}}>
								<path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
						</div>

						{/* Input Field */}
						<input
							type="text"
							name="entreprise-search"
							id="entreprise-search"
							placeholder={t('entreprises:searchPlaceholder')}
							value={searchTerm}
							onChange={handleSearchChange}
							className="tw-w-full tw-p-4 tw-pl-12 tw-pr-12 tw-pt-4 tw-pb-4 tw-border tw-border-[#D1D5DB] tw-rounded-4 tw-text-base tw-outline-none tw-box-border focus:tw-border-[#3B82F6] focus:tw-outline-none"
						/>

						{/* Clear Icon */}
						{searchTerm && searchTerm.length > 0 && (
							<button
								onClick={clearSearch}
								className="tw-absolute tw-right-4 tw-top-1/2 tw-transform tw-translate-y-[-50%] tw-z-10 tw-bg-none tw-border-none tw-cursor-pointer tw-p-2 tw-rounded-4 tw-flex tw-items-center tw-justify-center tw-text-[#6B7280] tw-transition-opacity tw-duration-200 hover:tw-opacity-70"
								aria-label="Clear search"
							>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
									<path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						)}
					</div>
				</div>
				<div className="tw-grid tw-grid-cols-4 tw-gap-10 tw-items-start tw-mb-8 grid-list-axial">
					<div className="axial-filters">
						<div className="tw-flex tw-justify-between tw-p-4 tw-bg-white">
							<span className="tw-font-bold">{t('entreprises:filters')}</span>
							<span className="tw-text-[#757575]">{displayEntreprises.length} {t(displayEntreprises.length === 1 ? 'entreprises:results' : 'entreprises:resultsPlural')}</span>
						</div>
						<FilterAccordion name={t('entreprises:industryType')} options={Array.from(industryTypes)} onChange={createFilterHandler('industryTypes')} />
						<FilterAccordion name={t('entreprises:productType')} options={Array.from(productTypes)} onChange={createFilterHandler('productTypes')} />
						<FilterAccordion name={t('entreprises:operationType')} options={Array.from(operationTypes)} onChange={createFilterHandler('operationTypes')} />
						<FilterAccordion name={t('entreprises:polymerType')} options={Array.from(polymerTypes)} onChange={createFilterHandler('polymerTypes')} />
						<FilterAccordion name={t('entreprises:region')} options={Array.from(regions)} onChange={createFilterHandler('regions')} />
					</div>
					<div className="tw-col-span-3 tw-flex tw-flex-col tw-gap-4 list-axial">
						<div className="tw-flex tw-justify-start">
							<FilterAlphabetical options={Array.from(alphabeticalOptions)} onChange={createFilterHandler('alphabetical')} />
						</div>
						{displayEntreprises.slice((currentPage - 1) * entreprisesPerPage, currentPage * entreprisesPerPage).map((entreprise) => (
							<EntrepriseRow entreprise={entreprise} key={entreprise.name} />
						))}
					</div>
				</div>
				<div className="pagination">
					{maxPages > 1 && (
						<>
							<button data-current={currentPage === 1 ? 'true' : 'false'} onClick={() => handlePageChange(1)}>1</button>
							{currentPage > 3 && <span>...</span>}
							{Array.from({ length: maxPages }, (_, i) => i + 1)
								.slice(Math.max(0, currentPage - 2), Math.min(maxPages, currentPage + 1))
								.filter(page => page > 1 && page < maxPages)
								.map((page) => (
								<button key={page} data-current={currentPage === page ? 'true' : 'false'} onClick={() => handlePageChange(page)}>{page}</button>
							))}
							{currentPage < maxPages - 2 && <span>...</span>}
							{maxPages > 1 && <button data-current={currentPage === maxPages ? 'true' : 'false'} onClick={() => handlePageChange(maxPages)}>{maxPages}</button>}
						</>
					)}
				</div>
			</div>
	);
}